import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { courtsApi, recordingsApi, storageApi } from '../../api';
import { useAuth } from '../../hooks/useAuth';
import Sidebar from './Sidebar';
import CourtStatusCard from './CourtStatusCard';
import SessionsTable from './SessionsTable';
import StoragePanel from './StoragePanel';
import KioskDeviceManager from './KioskDeviceManager';

const POLL_INTERVAL = 5000;

export default function AdminDashboard() {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [courts, setCourts] = useState([]);
  const [courtStatus, setCourtStatus] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [storageStats, setStorageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const loadCourtStatus = useCallback(async () => {
    try { const d = await courtsApi.statusOverview(); setCourtStatus(d); } catch {}
  }, []);

  const loadCourts = useCallback(async () => {
    try { const d = await courtsApi.list(); setCourts(d.results || d); } catch {}
  }, []);

  const loadSessions = useCallback(async () => {
    try { const d = await recordingsApi.list(); setSessions(d.results || d); } catch {}
  }, []);

  const loadStorage = useCallback(async () => {
    try { const d = await storageApi.stats(); setStorageStats(d); } catch {}
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.allSettled([loadCourtStatus(), loadCourts(), loadSessions(), loadStorage()]);
    setLoading(false);
  }, [loadCourtStatus, loadCourts, loadSessions, loadStorage]);

  useEffect(() => {
    loadAll();
    pollRef.current = setInterval(loadCourtStatus, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [loadAll, loadCourtStatus]);

  useEffect(() => {
    if (tab === 'sessions') loadSessions();
    if (tab === 'overview') loadStorage();
  }, [tab, loadSessions, loadStorage]);

  const activeCount = courtStatus.filter(c => c.status === 'recording').length;

  return (
    <div style={s.layout}>
      <Sidebar activeTab={tab} onTabChange={setTab} />

      <div style={s.main}>
        {/* Top header */}
        <header style={s.header}>
          <div>
            <h1 style={s.pageTitle}>
              {tab === 'overview'  && 'Overview'}
              {tab === 'sessions'  && 'Session History'}
              {tab === 'devices'   && 'Kiosk Devices'}
            </h1>
            <p style={s.pageSubtitle}>
              {tab === 'overview' && 'Live court status and storage usage'}
              {tab === 'sessions' && 'All recording sessions across all courts'}
              {tab === 'devices'  && 'Manage kiosk device assignments'}
            </p>
          </div>
          <div style={s.headerRight}>
            {activeCount > 0 && (
              <div style={s.livePill}>
                <span style={s.liveDot} />
                {activeCount} Live
              </div>
            )}
            <button onClick={loadAll} style={s.refreshBtn}>
              ↻ Refresh
            </button>
          </div>
        </header>

        {/* Content */}
        <div style={s.content}>
          {tab === 'overview' && (
            <div className="fade-in">
              <div style={s.courtsGrid}>
                {loading
                  ? [1,2,3,4].map(i => <div key={i} style={s.skeleton} />)
                  : courtStatus.map(c => <CourtStatusCard key={c.court_id} court={c} />)
                }
              </div>
              <StoragePanel stats={storageStats} onRefresh={loadStorage} />
            </div>
          )}
          {tab === 'sessions' && (
            <div className="fade-in">
              <SessionsTable sessions={sessions} loading={loading} />
            </div>
          )}
          {tab === 'devices' && (
            <div className="fade-in">
              <KioskDeviceManager courts={courts} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  layout: {
    display: 'flex', minHeight: '100vh',
    fontFamily: 'var(--font)',
  },
  main: {
    marginLeft: 'var(--sidebar-width)',
    flex: 1, display: 'flex', flexDirection: 'column',
    minHeight: '100vh',
  },
  header: {
    padding: '28px 32px 24px',
    background: 'var(--white)',
    borderBottom: '1px solid var(--gray-200)',
    display: 'flex', alignItems: 'flex-start',
    justifyContent: 'space-between',
    position: 'sticky', top: 0, zIndex: 30,
    boxShadow: 'var(--shadow-sm)',
  },
  pageTitle: {
    fontSize: 22, fontWeight: 700, color: 'var(--gray-900)',
    letterSpacing: '-0.3px', marginBottom: 3,
  },
  pageSubtitle: { fontSize: 13, color: 'var(--gray-500)' },
  headerRight: { display: 'flex', alignItems: 'center', gap: 10, paddingTop: 4 },
  livePill: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: 'var(--red-light)', color: 'var(--red)',
    fontSize: 13, fontWeight: 600,
    padding: '6px 14px', borderRadius: 50,
    border: '1px solid rgba(239,68,68,0.2)',
  },
  liveDot: {
    width: 7, height: 7, borderRadius: '50%',
    background: 'var(--red)',
    animation: 'pulse-dot 1.2s ease-in-out infinite',
    display: 'inline-block',
  },
  refreshBtn: {
    background: 'var(--gray-100)', border: '1px solid var(--gray-200)',
    borderRadius: 'var(--radius-sm)', padding: '7px 16px',
    fontSize: 13, fontWeight: 500, color: 'var(--gray-600)',
    cursor: 'pointer', fontFamily: 'var(--font)',
  },
  content: { padding: '28px 32px', flex: 1 },
  courtsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 16, marginBottom: 24,
  },
  skeleton: {
    height: 110, borderRadius: 'var(--radius)',
    background: 'linear-gradient(90deg, var(--gray-100) 25%, var(--gray-50) 50%, var(--gray-100) 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.4s infinite',
  },
};

// Shimmer animation
const style = document.createElement('style');
style.textContent = `@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`;
document.head.appendChild(style);
